<template>
  <v-dialog
    v-model="dialogVisible"
    persistent
    max-width="700"
    @keydown.esc="dismiss"
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white" large>mdi-bell-ring</v-icon>
        <span class="text-h5">Task Notification</span>
        <v-spacer></v-spacer>
        <v-chip color="white" text-color="primary" small>
          {{ task_notifications.length }}
          {{ task_notifications.length === 1 ? "update" : "updates" }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4" style="max-height: 500px; overflow-y: auto">
        <!-- New Assignments -->
        <div v-if="newTasks.length > 0" class="mb-4">
          <div class="subtitle-2 green--text text--darken-2 mb-2">
            <v-icon small color="green darken-2">mdi-plus-circle</v-icon>
            New Assignments ({{ newTasks.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="task in newTasks"
              :key="'new-' + task.id"
              class="green lighten-5 mb-1 rounded"
              style="cursor: pointer"
              @click="openDetail(task)"
            >
              <v-list-item-icon>
                <v-icon :color="priorityColor(task.priority)">
                  mdi-clipboard-plus
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ task.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="task.description" class="mr-2">
                    {{ truncate(task.description, 80) }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption">
                  <v-chip
                    x-small
                    :color="priorityColor(task.priority)"
                    dark
                    class="mr-1"
                  >
                    {{ priorityLabel(task.priority) }}
                  </v-chip>
                  <span v-if="task.category" class="mr-2">{{
                    task.category
                  }}</span>
                  <span v-if="task.scheduled_time" class="mr-2">
                    at {{ task.scheduled_time }}
                  </span>
                  <span v-if="task.assigned_by" class="grey--text">
                    Assigned by: {{ task.assigned_by.full_name }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Updated Assignments -->
        <div v-if="updatedTasks.length > 0" class="mb-4">
          <div class="subtitle-2 orange--text text--darken-2 mb-2">
            <v-icon small color="orange darken-2">mdi-pencil-circle</v-icon>
            Updated Assignments ({{ updatedTasks.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="task in updatedTasks"
              :key="'updated-' + task.id"
              class="orange lighten-5 mb-1 rounded"
              style="cursor: pointer"
              @click="openDetail(task)"
            >
              <v-list-item-icon>
                <v-icon :color="priorityColor(task.priority)">
                  mdi-clipboard-edit
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ task.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="task.description" class="mr-2">
                    {{ truncate(task.description, 80) }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption">
                  <v-chip
                    x-small
                    :color="priorityColor(task.priority)"
                    dark
                    class="mr-1"
                  >
                    {{ priorityLabel(task.priority) }}
                  </v-chip>
                  <span v-if="task.category" class="mr-2">{{
                    task.category
                  }}</span>
                  <span v-if="task.scheduled_time" class="mr-2">
                    at {{ task.scheduled_time }}
                  </span>
                  <span v-if="task.assigned_by" class="grey--text">
                    Assigned by: {{ task.assigned_by.full_name }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Cancelled Assignments -->
        <div v-if="cancelledTasks.length > 0" class="mb-4">
          <div class="subtitle-2 red--text text--darken-2 mb-2">
            <v-icon small color="red darken-2">mdi-cancel</v-icon>
            Cancelled Assignments ({{ cancelledTasks.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="task in cancelledTasks"
              :key="'cancelled-' + task.id"
              class="red lighten-5 mb-1 rounded"
              style="cursor: pointer"
              @click="openDetail(task)"
            >
              <v-list-item-icon>
                <v-icon color="red">mdi-clipboard-remove</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  class="font-weight-bold text-decoration-line-through"
                >
                  {{ task.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <span v-if="task.category" class="mr-2">{{
                    task.category
                  }}</span>
                  <span class="red--text">This task has been cancelled.</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Acknowledged Tasks -->
        <div v-if="acknowledgedTasks.length > 0" class="mb-4">
          <div class="subtitle-2 blue--text text--darken-2 mb-2">
            <v-icon small color="blue darken-2">mdi-check-decagram</v-icon>
            Acknowledged Tasks ({{ acknowledgedTasks.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="task in acknowledgedTasks"
              :key="'ack-' + task.id"
              class="blue lighten-5 mb-1 rounded"
              style="cursor: pointer"
              @click="openDetail(task)"
            >
              <v-list-item-icon>
                <v-icon color="blue darken-1">mdi-clipboard-check</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ task.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span class="blue--text text--darken-2">
                    Acknowledged by: {{ task.acknowledged_by_name }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="task.admin_comment">
                  <v-icon x-small class="mr-1">mdi-comment-text</v-icon>
                  "{{ task.admin_comment }}"
                </v-list-item-subtitle>
                <!-- User's completion note -->
                <v-list-item-subtitle v-if="task.completion_note">
                  <v-icon x-small class="mr-1">mdi-note-text</v-icon>
                  <span class="grey--text text--darken-1">Note: </span>
                  {{ task.completion_note }}
                </v-list-item-subtitle>
                <!-- Remarks history -->
                <v-list-item-subtitle
                  v-for="remark in task.remarks || []"
                  :key="'remark-' + remark.id"
                >
                  <v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>
                  <span class="grey--text text--darken-1">
                    {{ remark.user ? remark.user.full_name : "" }}
                    <span v-if="remark.status_set" class="font-italic"
                      >({{ remark.status_set }})</span
                    >:
                  </span>
                  {{ remark.remark }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption">
                  <v-chip
                    x-small
                    :color="priorityColor(task.priority)"
                    dark
                    class="mr-1"
                  >
                    {{ priorityLabel(task.priority) }}
                  </v-chip>
                  <span v-if="task.category" class="mr-2">{{
                    task.category
                  }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  icon
                  small
                  @click.stop="dismissAcknowledged(task)"
                  :loading="dismissingId === task.id"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <!-- Empty state -->
        <div v-if="task_notifications.length === 0" class="text-center pa-6">
          <v-icon large color="grey lighten-1" class="mb-3"
            >mdi-bell-check-outline</v-icon
          >
          <div class="grey--text">No new notifications.</div>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn
          text
          color="primary"
          @click="viewAllTasks"
          v-if="task_notifications.length > 0"
        >
          <v-icon left>mdi-clipboard-list</v-icon>
          View My Tasks
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" dark @click="dismiss">
          <v-icon left small>{{
            task_notifications.length > 0 ? "mdi-check" : "mdi-close"
          }}</v-icon>
          {{ task_notifications.length > 0 ? "Dismiss" : "Close" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Detail Dialog -->
    <task-detail-dialog v-model="showDetailDialog" :task="detailTask" />
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TaskDetailDialog from "./TaskDetailDialog.vue";

export default {
  name: "TaskNotificationDialog",
  components: { TaskDetailDialog },
  data() {
    return {
      audioContext: null,
      showDetailDialog: false,
      detailTask: null,
      dismissingId: null,
    };
  },
  computed: {
    ...mapGetters({
      task_notifications: "reminder/task_notifications",
      show_notification_dialog: "reminder/show_notification_dialog",
      user: "auth/user",
    }),
    dialogVisible: {
      get() {
        return this.show_notification_dialog;
      },
      set() {
        // Controlled entirely by store; prevent direct set
      },
    },
    newTasks() {
      return this.task_notifications.filter(
        (t) => t.notification_type === "new"
      );
    },
    updatedTasks() {
      return this.task_notifications.filter(
        (t) => t.notification_type === "updated"
      );
    },
    cancelledTasks() {
      return this.task_notifications.filter(
        (t) => t.notification_type === "cancelled"
      );
    },
    acknowledgedTasks() {
      return this.task_notifications.filter(
        (t) => t.notification_type === "acknowledged"
      );
    },
  },
  watch: {
    show_notification_dialog(val) {
      if (val && this.task_notifications.length > 0) {
        this.playNotificationSound();
      }
    },
  },
  methods: {
    ...mapActions({
      dismiss_task_notifications: "reminder/dismiss_task_notifications",
      dismiss_single_acknowledged: "reminder/dismiss_single_acknowledged",
    }),
    dismiss() {
      this.dismiss_task_notifications();
    },
    async dismissAcknowledged(task) {
      this.dismissingId = task.id;
      try {
        await this.dismiss_single_acknowledged(task.id);
      } catch (e) {
        console.error("[TaskNotification] Failed to dismiss:", e);
      }
      this.dismissingId = null;
    },
    viewAllTasks() {
      this.dismiss_task_notifications();
      // Navigate to the reminders/my-tasks area
      // Admins go to task management, regular users stay on current page
      if (this.can("route.task_management")) {
        this.$router.push("/task_management").catch(() => {});
      }
    },
    priorityColor(p) {
      return p === "very_urgent" ? "red" : p === "urgent" ? "orange" : "blue";
    },
    priorityLabel(p) {
      return p === "very_urgent"
        ? "Very Urgent"
        : p === "urgent"
        ? "Urgent"
        : "Basic";
    },
    openDetail(task) {
      this.detailTask = task;
      this.showDetailDialog = true;
    },
    truncate(str, maxLen) {
      if (!str) return "";
      return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
    },
    /**
     * Play a notification sound using the Web Audio API.
     * This works reliably in both Electron and standard browsers
     * without requiring an external audio file.
     */
    playNotificationSound() {
      try {
        var AudioContextClass =
          window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        var ctx = new AudioContextClass();

        // First tone: a pleasant notification chime (E5 = 659 Hz)
        var osc1 = ctx.createOscillator();
        var gain1 = ctx.createGain();
        osc1.type = "sine";
        osc1.frequency.setValueAtTime(659, ctx.currentTime);
        gain1.gain.setValueAtTime(0.3, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.3);

        // Second tone: higher chime (A5 = 880 Hz) after a short delay
        var osc2 = ctx.createOscillator();
        var gain2 = ctx.createGain();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.15);
        gain2.gain.setValueAtTime(0.01, ctx.currentTime);
        gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.15);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(ctx.currentTime + 0.15);
        osc2.stop(ctx.currentTime + 0.5);

        // Third tone: resolve (C#6 = 1109 Hz)
        var osc3 = ctx.createOscillator();
        var gain3 = ctx.createGain();
        osc3.type = "sine";
        osc3.frequency.setValueAtTime(1109, ctx.currentTime + 0.3);
        gain3.gain.setValueAtTime(0.01, ctx.currentTime);
        gain3.gain.setValueAtTime(0.25, ctx.currentTime + 0.3);
        gain3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.7);
        osc3.connect(gain3);
        gain3.connect(ctx.destination);
        osc3.start(ctx.currentTime + 0.3);
        osc3.stop(ctx.currentTime + 0.7);

        // Clean up context after sound finishes
        setTimeout(function () {
          ctx.close();
        }, 1000);
      } catch (e) {
        // Audio API not available or blocked; fail silently
        console.warn("[TaskNotification] Could not play sound:", e);
      }
    },
  },
};
</script>
